import React, { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import {
  ServiceEmailRequest,
  ServiceEmailSolicitudResumen,
} from "../../../models/ServiceEmailRequestModel";
import "./styles/ServiceEmailsSimpleTable.css";

type Props = {
  data: ServiceEmailRequest[];
  loading?: boolean;
  savingTimeId?: number | null;
  onOpen: (item: ServiceEmailRequest) => void;
  onReceivedTimeChange: (item: ServiceEmailRequest, time: string) => void | Promise<void>;
};

const toTimeInputValue = (raw?: string | null): string => {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    const match = String(raw).match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : "";
  }
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const collectServiceIds = (resumen: ServiceEmailSolicitudResumen[]): number[] => {
  const ids = new Set<number>();
  resumen.forEach((item) => {
    (item.service_ids ?? []).forEach((id) => {
      if (Number.isFinite(id) && id > 0) ids.add(id);
    });
  });
  return Array.from(ids).sort((a, b) => a - b);
};

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fallback below */
  }
  try {
    const el = document.createElement("textarea");
    el.value = value;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Vista simple tipo Excel del listado de correos.
 * - Plataforma: verde por defecto.
 * - Solicitud: verde si hay solicitudes (muestra cantidad), rojo si no.
 * - Servicios: verde si hay servicios vinculados.
 * - IDs Servicios: ids de servicios vinculados.
 * - Fecha Llego: editable (hora).
 */
const ServiceEmailsSimpleTable: React.FC<Props> = ({
  data,
  loading,
  savingTimeId = null,
  onOpen,
  onReceivedTimeChange,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [draftTimes, setDraftTimes] = useState<Record<number, string>>({});

  useEffect(() => {
    setDraftTimes((prev) => {
      const next: Record<number, string> = {};
      data.forEach((item) => {
        if (item.id == null) return;
        // Conservar draft mientras se edita esa fila.
        if (prev[item.id] !== undefined && savingTimeId === item.id) {
          next[item.id] = prev[item.id];
        } else {
          next[item.id] = toTimeInputValue(item.received_at);
        }
      });
      return next;
    });
  }, [data, savingTimeId]);

  const rows = [...data].sort((a, b) => {
    const ta = a.received_at ? new Date(a.received_at).getTime() : Number.POSITIVE_INFINITY;
    const tb = b.received_at ? new Date(b.received_at).getTime() : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return (a.id ?? 0) - (b.id ?? 0);
  });

  const handleCopy = async (key: string, value: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const text = value.trim();
    if (!text) return;
    const ok = await copyText(text);
    if (!ok) return;
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((current) => (current === key ? null : current));
    }, 1200);
  };

  const commitTime = (item: ServiceEmailRequest) => {
    if (item.id == null) return;
    const next = (draftTimes[item.id] ?? "").trim();
    const current = toTimeInputValue(item.received_at);
    if (!/^\d{2}:\d{2}$/.test(next) || next === current) return;
    void onReceivedTimeChange(item, next);
  };

  return (
    <div className="emails-simple-wrap">
      <table className="emails-simple-table">
        <thead>
          <tr>
            <th className="emails-simple-th--correo">correo</th>
            <th>Empresa</th>
            <th>Fecha Llego</th>
            <th>Emisor</th>
            <th className="emails-simple-th--status">PLATAFORMA</th>
            <th className="emails-simple-th--status">SOLICITUD</th>
            <th className="emails-simple-th--status">SERVICIOS</th>
            <th className="emails-simple-th--ids">IDs Servicios</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const resumen: ServiceEmailSolicitudResumen[] = item.solicitudes_resumen ?? [];
            const solicitudesCount = Math.max(
              resumen.length,
              Number(item.solicitudes_count ?? 0) || 0
            );
            const hasSolicitud = solicitudesCount > 0;
            const serviceIds = collectServiceIds(resumen);
            const hasServices =
              serviceIds.length > 0 || resumen.some((row) => row.has_services);
            const subject = (item.subject || "").trim();
            const sender = (item.sender_email || "").trim();
            const subjectKey = `subject-${item.id}`;
            const senderKey = `sender-${item.id}`;
            const timeValue =
              item.id != null ? draftTimes[item.id] ?? toTimeInputValue(item.received_at) : "";
            const isSaving = item.id != null && savingTimeId === item.id;

            return (
              <tr key={item.id} onClick={() => onOpen(item)}>
                <td className="emails-simple-td--correo">
                  <div className="emails-simple-copy-row">
                    <strong title={subject || undefined}>{subject || "—"}</strong>
                    {subject ? (
                      <button
                        type="button"
                        className={`emails-simple-copy-btn${
                          copiedKey === subjectKey ? " is-copied" : ""
                        }`}
                        onClick={(event) => handleCopy(subjectKey, subject, event)}
                        title={copiedKey === subjectKey ? "Copiado" : "Copiar asunto"}
                        aria-label={
                          copiedKey === subjectKey
                            ? "Asunto copiado"
                            : `Copiar asunto: ${subject}`
                        }
                      >
                        {copiedKey === subjectKey ? (
                          <Check size={14} strokeWidth={2.25} aria-hidden />
                        ) : (
                          <Copy size={14} strokeWidth={2} aria-hidden />
                        )}
                      </button>
                    ) : null}
                  </div>
                </td>
                <td>{item.sender_company_name || "—"}</td>
                <td className="emails-simple-td--time" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="time"
                    className={`emails-simple-time-input${isSaving ? " is-saving" : ""}`}
                    value={timeValue}
                    disabled={isSaving}
                    onChange={(e) => {
                      if (item.id == null) return;
                      setDraftTimes((prev) => ({ ...prev, [item.id]: e.target.value }));
                    }}
                    onBlur={() => commitTime(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        if (item.id == null) return;
                        setDraftTimes((prev) => ({
                          ...prev,
                          [item.id]: toTimeInputValue(item.received_at),
                        }));
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    title="Cambiar hora de llegada"
                    aria-label={`Hora de llegada del correo ${subject || item.id}`}
                  />
                </td>
                <td className="emails-simple-td--sender">
                  <div className="emails-simple-copy-row">
                    <span title={sender || undefined}>{sender || "—"}</span>
                    {sender ? (
                      <button
                        type="button"
                        className={`emails-simple-copy-btn${
                          copiedKey === senderKey ? " is-copied" : ""
                        }`}
                        onClick={(event) => handleCopy(senderKey, sender, event)}
                        title={copiedKey === senderKey ? "Copiado" : "Copiar emisor"}
                        aria-label={
                          copiedKey === senderKey
                            ? "Emisor copiado"
                            : `Copiar emisor: ${sender}`
                        }
                      >
                        {copiedKey === senderKey ? (
                          <Check size={14} strokeWidth={2.25} aria-hidden />
                        ) : (
                          <Copy size={14} strokeWidth={2} aria-hidden />
                        )}
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="emails-simple-cell is-ok" title="Plataforma" />
                <td
                  className={`emails-simple-cell emails-simple-cell--count ${
                    hasSolicitud ? "is-ok" : "is-bad"
                  }`}
                  title={
                    hasSolicitud
                      ? `${solicitudesCount} solicitud${solicitudesCount !== 1 ? "es" : ""}`
                      : "Sin solicitud"
                  }
                >
                  {solicitudesCount}
                </td>
                <td
                  className={`emails-simple-cell ${hasServices ? "is-ok" : "is-bad"}`}
                  title={hasServices ? `${serviceIds.length} servicio(s)` : "Sin servicios"}
                />
                <td className="emails-simple-td--ids">
                  {serviceIds.length ? serviceIds.join(", ") : "—"}
                </td>
              </tr>
            );
          })}
          {!loading && data.length === 0 && (
            <tr>
              <td colSpan={8} className="emails-simple-empty">
                No hay correos para los filtros seleccionados.
              </td>
            </tr>
          )}
          {loading && data.length === 0 && (
            <tr>
              <td colSpan={8} className="emails-simple-empty">
                Cargando correos…
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ServiceEmailsSimpleTable;
